import React from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { Brain, Code, Users, Briefcase } from 'lucide-react';

interface SkillDistributionProps {
  skills: {
    category: string;
    percentage: number;
    icon: React.ReactNode;
    color: string;
  }[];
}

const COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B'];

const SkillDistribution: React.FC<SkillDistributionProps> = ({ skills }) => {
  const data = skills.map(skill => ({
    name: skill.category,
    value: skill.percentage,
    icon: skill.icon,
    color: skill.color
  }));

  const totalPercentage = skills.reduce((acc, skill) => acc + skill.percentage, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-xl shadow-md"
    >
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Skill Distribution</h3>
      <div className="flex items-center justify-between">
        <div className="w-1/2 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-1/2 space-y-4">
          {skills.map((skill, index) => (
            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${skill.color.replace('text-', 'bg-').replace('600', '100')} mr-3`}>
                  {skill.icon}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{skill.category}</div>
                  <div className="text-sm text-gray-500">{skill.percentage}% proficiency</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Distribution Analysis</h4>
        <p className="text-gray-600 text-sm leading-relaxed">
          Your skill distribution shows a {Math.max(...skills.map(s => s.percentage))}% focus on {
            skills.find(s => s.percentage === Math.max(...skills.map(s => s.percentage)))?.category
          }. This {
            Math.max(...skills.map(s => s.percentage)) > 35 ? 'strong specialization' : 'balanced distribution'
          } suggests {
            Math.max(...skills.map(s => s.percentage)) > 35 
              ? 'you\'re developing expertise in a specific area' 
              : 'you\'re maintaining a versatile skill set'
          }. Consider {
            Math.max(...skills.map(s => s.percentage)) > 35
              ? 'broadening your skill set in complementary areas'
              : 'deepening your expertise in one primary area'
          } to enhance your career opportunities.
        </p>
      </div>
    </motion.div>
  );
};

export default SkillDistribution;